import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { glopicApi } from "@/lib/glopic-api";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import ConfirmModal from "@/components/base/ConfirmModal";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import TableSortHeader from "@/components/base/TableSortHeader";

interface VipPackage {
  package_id: string;
  name: string;
  price: number;
  original_price: number | null;
  price_usd: number | null;
  original_price_usd: number | null;
  duration_days: number;
  points_gift: number;
  is_auto_renew: boolean;
  apple_product_id: string | null;
  enabled: boolean;
  is_recommended: boolean;
  sort: number | null;
  benefits: unknown[];
}

function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const payload = err.response?.data as { detail?: string; message?: string } | undefined;
    return payload?.detail || payload?.message || err.message || fallback;
  }
  return fallback;
}

export default function AiVipPackagesPage() {
  const { canAccess } = useAdminAuth();
  const [packages, setPackages] = useState<VipPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("sort");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [showModal, setShowModal] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<string>("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VipPackage | null>(null);
  const [originalAppleProductId, setOriginalAppleProductId] = useState("");
  const [appleProductConfirmed, setAppleProductConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [changeReason, setChangeReason] = useState("");
  const [formData, setFormData] = useState({
    package_id: "",
    name: "",
    price: "",
    original_price: "",
    price_usd: "",
    original_price_usd: "",
    duration_days: "",
    points_gift: "",
    is_auto_renew: false,
    apple_product_id: "",
    enabled: true,
    is_recommended: false,
    sort: "0",
  });

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "sort");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await glopicApi.get("/vip/packages");
      const data = response.data.data;
      // Backend returns a list directly or paginated
      const items = Array.isArray(data) ? data : (data.items ?? []);
      setPackages(items);
      setTotal(Array.isArray(data) ? data.length : (data.total ?? items.length));
    } catch (err) {
      console.error("Fetch VIP packages error:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortField, sortDir, page, pageSize]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleEnabled = async (pkg: VipPackage) => {
    const reason = window.prompt("请输入套餐状态变更原因（将写入不可变审计）")?.trim();
    if (!reason) return;
    try {
      await glopicApi.patch(`/vip/packages/${pkg.package_id}/enabled`, {
        enabled: !pkg.enabled,
        reason,
      });
      fetchPackages();
    } catch (err) {
      console.error("Toggle enabled error:", err);
    }
  };

  const openCreate = () => {
    setEditingPackageId("");
    setFormData({
      package_id: "",
      name: "",
      price: "",
      original_price: "",
      price_usd: "",
      original_price_usd: "",
      duration_days: "",
      points_gift: "",
      is_auto_renew: false,
      apple_product_id: "",
      enabled: true,
      is_recommended: false,
      sort: "0",
    });
    setOriginalAppleProductId("");
    setAppleProductConfirmed(false);
    setChangeReason("");
    setShowModal(true);
  };

  const openEdit = (pkg: VipPackage) => {
    setEditingPackageId(pkg.package_id);
    setFormData({
      package_id: pkg.package_id,
      name: pkg.name,
      price: String(pkg.price),
      original_price: pkg.original_price ? String(pkg.original_price) : "",
      price_usd: pkg.price_usd != null ? String(pkg.price_usd) : "",
      original_price_usd: pkg.original_price_usd != null ? String(pkg.original_price_usd) : "",
      duration_days: String(pkg.duration_days),
      points_gift: String(pkg.points_gift),
      is_auto_renew: pkg.is_auto_renew,
      apple_product_id: pkg.apple_product_id || "",
      enabled: pkg.enabled,
      is_recommended: pkg.is_recommended,
      sort: String(pkg.sort ?? 0),
    });
    setOriginalAppleProductId(pkg.apple_product_id || "");
    setAppleProductConfirmed(false);
    setChangeReason("");
    setShowModal(true);
  };

  const promptDelete = (pkg: VipPackage) => {
    setDeleteTarget(pkg);
    setDeleteConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    // Note: GlowPic API does not expose a DELETE /vip/packages/{id} endpoint.
    // Show informational toast only.
    showToast(`删除操作请联系后端操作`, "error");
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const savePackage = async () => {
    const parsedDurationDays = Number.parseInt(formData.duration_days, 10);
    const normalizedAppleProductId = formData.apple_product_id.trim();
    const productIdChanged = Boolean(
      editingPackageId && normalizedAppleProductId !== originalAppleProductId,
    );
    const payload = {
      reason: changeReason.trim() || (editingPackageId ? "管理后台直接修改套餐" : "管理后台创建套餐"),
      package_id: formData.package_id,
      name: formData.name,
      price: parseFloat(formData.price) || 0,
      original_price: formData.original_price ? parseFloat(formData.original_price) : null,
      price_usd: formData.price_usd ? parseFloat(formData.price_usd) : null,
      original_price_usd: formData.original_price_usd ? parseFloat(formData.original_price_usd) : null,
      duration_days: Number.isNaN(parsedDurationDays) ? 30 : parsedDurationDays,
      points_gift: parseInt(formData.points_gift) || 0,
      is_auto_renew: formData.is_auto_renew,
      apple_product_id: normalizedAppleProductId,
      ...(productIdChanged ? { expected_apple_product_id: originalAppleProductId } : {}),
      enabled: formData.enabled,
      is_recommended: formData.is_recommended,
      sort: parseInt(formData.sort) || 0,
    };

    try {
      setSaving(true);
      if (editingPackageId) {
        await glopicApi.put(`/vip/packages/${editingPackageId}`, payload);
        setShowModal(false);
        showToast("套餐已更新", "success");
      } else {
        await glopicApi.post("/vip/packages", payload);
        setShowModal(false);
        showToast("套餐已创建", "success");
      }
      await fetchPackages();
    } catch (err) {
      showToast(extractApiError(err, "保存失败，请重试"), "error");
      console.error("Save package error:", err);
    } finally {
      setSaving(false);
    }
  };

  const canModify = canAccess("ai_vip");
  const normalizedAppleProductId = formData.apple_product_id.trim();
  const productIdChanged = Boolean(
    editingPackageId && normalizedAppleProductId !== originalAppleProductId,
  );
  const totalPages = Math.ceil(total / pageSize);
  const formatPrice = (value: number | null | undefined, symbol: "¥" | "$") => {
    if (value == null) return "-";
    return `${symbol}${Number(value).toFixed(2)}`;
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text-primary">VIP 套餐管理</h2>
        {canModify && (
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-xl text-sm font-medium text-white bg-brand hover:bg-brand/90 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-add-line" />
            新建套餐
          </button>
        )}
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-800">
        <p className="font-semibold mb-1">渠道价格说明</p>
        <p>人民币/美元价格仅用于 Web、Android 等非 Apple 收银台。iOS 实际金额与币种由 App Store Connect 和用户当前 App Store 地区决定；积分礼包是 GlowPic App 发放积分的配置来源。</p>
      </div>

      <FilterBar
        search={search}
        onSearchChange={(val) => { setSearch(val); setPage(1); }}
        searchPlaceholder="搜索套餐名称或ID..."
        onReset={() => { setSearch(""); setSortField("sort"); setSortDir("asc"); setPage(1); }}
      />

      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">套餐</th>
                <TableSortHeader label="Web/Android 价格" field="price" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">时长</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">积分礼包</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">Apple Product ID</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">自动续费</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">状态</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">推荐</th>
                <th className="text-right px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : packages.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-vip-crown-line text-3xl mb-2 block" />
                    暂无 VIP 套餐
                  </td>
                </tr>
              ) : (
                packages.map((pkg) => (
                  <tr key={pkg.package_id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <div>
                        <p className="text-text-primary font-medium text-sm">{pkg.name}</p>
                        <p className="text-text-muted text-[11px] font-mono">{pkg.package_id}</p>
                      </div>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <span className="text-text-primary font-semibold">{formatPrice(pkg.price, "¥")}</span>
                          {pkg.original_price && Number(pkg.original_price) > Number(pkg.price) && (
                            <span className="text-text-muted text-xs line-through">{formatPrice(pkg.original_price, "¥")}</span>
                          )}
                          <span className="text-[10px] text-text-muted">CNY</span>
                        </div>
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          <span className="text-text-primary font-semibold">{formatPrice(pkg.price_usd, "$")}</span>
                          {pkg.original_price_usd && pkg.price_usd != null && Number(pkg.original_price_usd) > Number(pkg.price_usd) && (
                            <span className="text-text-muted text-xs line-through">{formatPrice(pkg.original_price_usd, "$")}</span>
                          )}
                          <span className="text-[10px] text-text-muted">USD</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4 text-text-secondary text-sm">
                      {pkg.duration_days >= 99999 ? "终身" : `${pkg.duration_days} 天`}
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4 font-mono text-text-primary text-sm">
                      +{pkg.points_gift}
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <span className="font-mono text-[11px] text-text-secondary break-all">
                        {pkg.apple_product_id || "-"}
                      </span>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      {pkg.is_auto_renew ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-500">
                          <i className="ri-refresh-line" /> 开启
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-text-muted">
                          <i className="ri-close-line" /> 关闭
                        </span>
                      )}
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      {canModify ? (
                        <button
                          onClick={() => toggleEnabled(pkg)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-colors cursor-pointer ${
                            pkg.enabled
                              ? "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
                              : "bg-text-muted/10 text-text-muted hover:bg-text-muted/20"
                          }`}
                        >
                          <i className={pkg.enabled ? "ri-checkbox-circle-line" : "ri-close-circle-line"} />
                          {pkg.enabled ? "启用" : "禁用"}
                        </button>
                      ) : (
                        <span className={`inline-flex px-2 py-1 rounded-full text-[11px] font-medium ${
                          pkg.enabled ? "bg-emerald-500/10 text-emerald-500" : "bg-text-muted/10 text-text-muted"
                        }`}>
                          {pkg.enabled ? "启用" : "禁用"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${
                        pkg.is_recommended ? "bg-amber-500/10 text-amber-500" : "bg-surface text-text-muted"
                      }`}>
                        <i className={pkg.is_recommended ? "ri-star-fill" : "ri-star-line"} />
                        {pkg.is_recommended ? "推荐" : "普通"}
                      </span>
                    </td>
                    <td className="px-3 md:px-5 py-3 md:py-4 text-right">
                      <div className="flex items-center justify-end gap-1 md:gap-2">
                        {canModify && (
                          <button
                            onClick={() => openEdit(pkg)}
                            className="w-7 h-7 md:w-8 md:h-8 rounded-md flex items-center justify-center hover:bg-surface text-text-secondary hover:text-brand transition-colors cursor-pointer"
                            title="编辑"
                          >
                            <i className="ri-edit-line text-sm md:text-base" />
                          </button>
                        )}
                        {canModify && (
                          <button
                            onClick={() => promptDelete(pkg)}
                            className="w-7 h-7 md:w-8 md:h-8 rounded-md flex items-center justify-center hover:bg-surface text-text-secondary hover:text-red-500 transition-colors cursor-pointer"
                            title="删除"
                          >
                            <i className="ri-delete-bin-line text-sm md:text-base" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-text-primary mb-5">
              {editingPackageId ? "编辑套餐" : "新建套餐"}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1">套餐ID</label>
                  <input
                    value={formData.package_id}
                    onChange={(e) => setFormData({ ...formData, package_id: e.target.value })}
                    placeholder="如: vip_monthly"
                    disabled={!!editingPackageId}
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {editingPackageId && <p className="text-text-muted text-[11px] mt-1">套餐ID 不可修改</p>}
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">名称</label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="如: 月度会员"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Web/Android 价格 (¥)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="28.00"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Web/Android 原价 (¥)</label>
                  <input
                    type="number"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="38.00"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">时长 (天)</label>
                  <input
                    type="number"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                    placeholder="30"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Web/Android 价格 ($)</label>
                  <input
                    type="number"
                    value={formData.price_usd}
                    onChange={(e) => setFormData({ ...formData, price_usd: e.target.value })}
                    placeholder="3.99"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Web/Android 原价 ($)</label>
                  <input
                    type="number"
                    value={formData.original_price_usd}
                    onChange={(e) => setFormData({ ...formData, original_price_usd: e.target.value })}
                    placeholder="5.99"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-text-secondary mb-1">App 积分礼包</label>
                  <input
                    type="number"
                    value={formData.points_gift}
                    onChange={(e) => setFormData({ ...formData, points_gift: e.target.value })}
                    placeholder="100"
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                  />
                  <p className="text-text-muted text-[11px] mt-1">购买成功后由 GlowPic 按此配置发放积分</p>
                </div>
                <div>
                  <label className="block text-sm text-text-secondary mb-1">Apple Product ID</label>
                  <input
                    value={formData.apple_product_id}
                    onChange={(e) => {
                      setFormData({ ...formData, apple_product_id: e.target.value });
                      setAppleProductConfirmed(false);
                    }}
                    placeholder="com.nanano.vip.monthly"
                    disabled={editingPackageId === "monthly_auto"}
                    className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                  {editingPackageId === "monthly_auto" && (
                    <p className="text-text-muted text-[11px] mt-1">
                      连续包月与 Apple 自动续费订阅绑定，请通过新商品迁移流程调整
                    </p>
                  )}
                </div>
              </div>
              {productIdChanged && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p className="font-medium">Apple Product ID 将直接切换</p>
                  <p className="mt-1 break-all text-xs">
                    {originalAppleProductId} → {normalizedAppleProductId || "（空）"}
                  </p>
                  <p className="mt-2 text-xs">
                    保存时系统会自动检查并发修改、重复商品和 Apple 待支付订单。
                  </p>
                  <label className="mt-3 flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={appleProductConfirmed}
                      onChange={(event) => setAppleProductConfirmed(event.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-brand"
                    />
                    <span>已确认新商品已在 App Store Connect 配置并可用</span>
                  </label>
                </div>
              )}
              <div>
                <label className="block text-sm text-text-secondary mb-1">排序</label>
                <input
                  type="number"
                  value={formData.sort}
                  onChange={(e) => setFormData({ ...formData, sort: e.target.value })}
                  placeholder="0"
                  className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">
                  变更原因{productIdChanged ? "（Product ID 变更时必填）" : "（选填）"}
                </label>
                <input
                  value={changeReason}
                  onChange={(event) => setChangeReason(event.target.value)}
                  placeholder={productIdChanged ? "请输入 Product ID 变更原因" : "可选填写，留空将自动记录后台修改"}
                  className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                />
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_auto_renew}
                    onChange={(e) => setFormData({ ...formData, is_auto_renew: e.target.checked })}
                    className="w-4 h-4 accent-brand"
                  />
                  <span className="text-sm text-text-primary">自动续费</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_recommended}
                    onChange={(e) => setFormData({ ...formData, is_recommended: e.target.checked })}
                    className="w-4 h-4 accent-brand"
                  />
                  <span className="text-sm text-text-primary">推荐套餐</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="w-4 h-4 accent-brand"
                  />
                  <span className="text-sm text-text-primary">启用</span>
                </label>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-text-secondary border border-surface-border hover:bg-surface transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={savePackage}
                disabled={
                  saving
                  || !formData.package_id
                  || !formData.name
                  || !formData.price
                  || !formData.duration_days
                  || !normalizedAppleProductId
                  || (productIdChanged && (!appleProductConfirmed || !changeReason.trim()))
                }
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white bg-brand hover:bg-brand/90 disabled:opacity-40 transition-colors cursor-pointer"
              >
                {saving ? "保存中..." : editingPackageId ? "保存修改" : "创建套餐"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl text-sm font-medium text-white shadow-lg transition-all ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          <div className="flex items-center gap-2">
            <i className={toast.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            {toast.message}
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title="删除套餐"
        description={`确认删除套餐 "${deleteTarget?.name}"？删除后将无法恢复，已购买的订阅不受影响。`}
        confirmText="确认删除"
        confirmDanger
        onConfirm={executeDelete}
        onCancel={() => { setDeleteConfirmOpen(false); setDeleteTarget(null); }}
      />
    </div>
  );
}
